import { useState, useMemo, useCallback } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { KpiStrip } from '@/components/common/KpiStrip';
import { PageLoader } from '@/components/common/LoadingSpinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown as ChevronDownIcon,
  Search,
  Filter,
  ShieldCheck,
  ShieldAlert,
  ShieldAlert as ShieldX,
  Shield,
} from '@/utils/icons';
import {
  useSecuritySummary,
  useFirewallGroups,
  useFirewallHitsByNode,
  useRulesPerNetwork,
  useDeniedTrafficTrend,
  useDeniedPacketsForPeriod,
} from '@/hooks/useSecurity';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv } from '@/utils/csv';
import { CORE, NEEVE, NEUTRAL } from '@/utils/dls-tokens';
import { FilterChip } from '@/components/common/FilterChip';

function getScoreColor(score: number): string {
  if (score >= 80) return 'dls-text-status-online';
  if (score >= 60) return 'dls-text-status-warning';
  return 'dls-text-status-offline';
}

function formatBigNum(val: string | number): string {
  const n = typeof val === 'string' ? parseInt(val, 10) : val;
  if (isNaN(n)) return '0';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatBytes(val: string | number): string {
  const n = typeof val === 'string' ? parseInt(val, 10) : val;
  if (isNaN(n)) return '0 B';
  if (n >= 1_099_511_627_776) return `${(n / 1_099_511_627_776).toFixed(1)} TB`;
  if (n >= 1_073_741_824) return `${(n / 1_073_741_824).toFixed(1)} GB`;
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

// Cloud Connector profiles: "Virtual", "Unified Cloud Gateway"
// Everything else (Edge, Virtual Edge, etc.) = Edge
const CLOUD_PROFILES = ['Virtual', 'Unified Cloud Gateway'];

function buildNetworkLabel(
  name: string,
  nodeName: string | null,
  _segmentCategory: string | null,
  _activeSegment?: string
): { label: string } {
  const site = nodeName ?? name;
  const label = site.length > 22 ? site.slice(0, 20) + '...' : site;
  return { label };
}


interface SelectedNetwork {
  networkName: string;
  nodeName: string | null;
  groupIds: string[];
}

export function SecurityPolicies() {
  const { summary, loading: summaryLoading } = useSecuritySummary();

  // Period pickers
  const [deniedTrendPeriod, setDeniedTrendPeriod] = useState<string>('30d');
  const [deniedPacketsPeriod, setDeniedPacketsPeriod] = useState<string>('24h');
  const [rulesTypeFilter, setRulesTypeFilter] = useState<'all' | 'system' | 'custom'>('all');
  const [customUsageFilter, setCustomUsageFilter] = useState<'all' | 'in-use' | 'not-in-use'>('all');

  // Chart sort: 'count' (desc by total rules) or 'alpha' (A-Z by site name)
  const [wanRulesSort, setWanRulesSort] = useState<'count' | 'alpha'>('count');
  const [lanRulesSort, setLanRulesSort] = useState<'count' | 'alpha'>('count');
  const [deniedPacketsSort, setDeniedPacketsSort] = useState<'count' | 'alpha'>('count');

  // Profile filter for Rules Per WAN/LAN charts: 'all' | 'edge' | 'cc'
  const [rulesProfileFilter, setRulesProfileFilter] = useState<'all' | 'edge' | 'cc'>('all');

  // Chart slice: percentage of items to display
  const [chartSlice, setChartSlice] = useState<number>(0.25);

  // Cross-chart filter state
  const [selectedNetwork, setSelectedNetwork] = useState<SelectedNetwork | null>(null);

  // Firewall groups table state
  const [groupSearch, setGroupSearch] = useState('');
  const [groupPermFilter, setGroupPermFilter] = useState('');
  const [groupUsageFilter, setGroupUsageFilter] = useState<'' | 'in-use' | 'unused'>('');
  const [groupPage, setGroupPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const groupLimit = 25;

  const { groups: rawGroups, total: groupTotal, totalPages: groupTotalPages, loading: groupsLoading } = useFirewallGroups({
    ids: selectedNetwork?.groupIds.length ? selectedNetwork.groupIds : undefined,
    search: selectedNetwork ? undefined : groupSearch || undefined,
    hasOverlyPermissive: selectedNetwork ? undefined : groupPermFilter || undefined,
    page: selectedNetwork ? 1 : groupPage,
    limit: selectedNetwork ? 100 : groupLimit,
  });

  // Client-side usage filter (in-use = has linked networks, unused = no linked networks)
  const groups = useMemo(() => {
    if (!groupUsageFilter || selectedNetwork) return rawGroups;
    return rawGroups.filter((g) =>
      groupUsageFilter === 'in-use' ? g.usedBy.length > 0 : g.usedBy.length === 0
    );
  }, [rawGroups, groupUsageFilter, selectedNetwork]);

  // Firewall charts data
  const { data: rulesPerNetwork } = useRulesPerNetwork();
  const { data: deniedTrend } = useDeniedTrafficTrend(deniedTrendPeriod, selectedNetwork?.networkName, selectedNetwork?.nodeName ?? undefined);
  const { data: hitsByNode } = useFirewallHitsByNode();
  const { data: deniedPacketsPeriodData } = useDeniedPacketsForPeriod(deniedPacketsPeriod);


  // Helper: pick allow/deny counts based on type filter
  const getRuleCounts = useCallback((r: typeof rulesPerNetwork[number]) => {
    if (rulesTypeFilter === 'system') return { allow: r.systemAllowRules, deny: r.systemDenyRules };
    if (rulesTypeFilter === 'custom') return { allow: r.customAllowRules, deny: r.customDenyRules };
    return { allow: r.allowRules, deny: r.denyRules };
  }, [rulesTypeFilter]);

  // Networks with denied traffic (for custom "In Use" / "Not In Use" filter)
  const networksWithDeniedTraffic = useMemo(() => {
    const set = new Set<string>();
    for (const h of hitsByNode) {
      if (parseInt(h.deniedPackets, 10) > 0) {
        set.add(`${h.nodeName ?? ''}::${h.name}`);
      }
    }
    return set;
  }, [hitsByNode]);

  const matchesCustomUsage = useCallback((siteName: string, networkName: string) => {
    if (rulesTypeFilter !== 'custom' || customUsageFilter === 'all') return true;
    const key = `${siteName}::${networkName}`;
    return customUsageFilter === 'in-use'
      ? networksWithDeniedTraffic.has(key)
      : !networksWithDeniedTraffic.has(key);
  }, [rulesTypeFilter, customUsageFilter, networksWithDeniedTraffic]);

  // Helper: check if a network matches the profile filter
  const matchesProfileFilter = useCallback((profile: string | null) => {
    if (rulesProfileFilter === 'all') return true;
    const isCC = profile != null && CLOUD_PROFILES.includes(profile);
    return rulesProfileFilter === 'cc' ? isCC : !isCC;
  }, [rulesProfileFilter]);

  // Chart data: Rules Per WAN
  const wanRulesChartData = useMemo(() => {
    const items = rulesPerNetwork
      .filter((r) => r.segmentCategory === 'WAN' && matchesProfileFilter(r.nodeProfile))
      .map((r) => {
        const { allow, deny } = getRuleCounts(r);
        const { label } = buildNetworkLabel(r.name, r.nodeName, r.segmentCategory, undefined);
        return { name: label, siteName: r.nodeName ?? r.name, networkName: r.name, Allow: allow, Deny: deny, total: allow + deny, groupIds: r.firewallGroupIds ?? [] };
      })
      .filter((r) => r.total > 0 && matchesCustomUsage(r.siteName, r.networkName));
    if (wanRulesSort === 'alpha') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      items.sort((a, b) => b.total - a.total);
    }
    const count = Math.max(1, Math.ceil(items.length * chartSlice));
    return items.slice(0, count);
  }, [rulesPerNetwork, getRuleCounts, matchesProfileFilter, matchesCustomUsage, wanRulesSort, chartSlice]);

  // Chart data: Rules Per LAN
  const lanRulesChartData = useMemo(() => {
    const items = rulesPerNetwork
      .filter((r) => r.segmentCategory !== 'WAN' && matchesProfileFilter(r.nodeProfile))
      .map((r) => {
        const { allow, deny } = getRuleCounts(r);
        const { label } = buildNetworkLabel(r.name, r.nodeName, r.segmentCategory, undefined);
        return { name: label, siteName: r.nodeName ?? r.name, networkName: r.name, Allow: allow, Deny: deny, total: allow + deny, groupIds: r.firewallGroupIds ?? [] };
      })
      .filter((r) => r.total > 0 && matchesCustomUsage(r.siteName, r.networkName));
    if (lanRulesSort === 'alpha') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      items.sort((a, b) => b.total - a.total);
    }
    const count = Math.max(1, Math.ceil(items.length * chartSlice));
    return items.slice(0, count);
  }, [rulesPerNetwork, getRuleCounts, matchesProfileFilter, matchesCustomUsage, lanRulesSort, chartSlice]);

  const deniedTrendData = useMemo(() => {
    return deniedTrend.map((d) => ({
      bucket: d.bucket,
      // null = no snapshots for this bucket → rendered as a gap, not a zero
      Bytes: d.deniedBytes === null ? null : parseInt(d.deniedBytes, 10) || 0,
      Packets: d.deniedPackets === null ? null : parseInt(d.deniedPackets, 10) || 0,
    }));
  }, [deniedTrend]);

  const deniedTrendHasData = useMemo(
    () => deniedTrendData.some((d) => d.Bytes !== null || d.Packets !== null),
    [deniedTrendData]
  );

  // Buckets predating the full-polling fix — shaded as reconstructed/partial
  const deniedPartialRegion = useMemo(() => {
    const partial = deniedTrend.filter((d) => d.isPartial);
    if (partial.length < 2) return null;
    return { x1: partial[0].bucket, x2: partial[partial.length - 1].bucket };
  }, [deniedTrend]);

  const hitsByNodeData = useMemo(() => {
    const items = hitsByNode.map((h) => {
      const { label } = buildNetworkLabel(h.name, h.nodeName, null, undefined);
      return {
        name: label,
        siteName: h.nodeName ?? h.name,
        networkName: h.name,
        deniedPackets: parseInt(h.deniedPackets, 10) || 0,
        deniedBytes: parseInt(h.deniedBytes, 10) || 0,
        ruleCount: h.ruleCount,
        groupIds: h.firewallGroupIds ?? [],
      };
    });
    if (deniedPacketsSort === 'alpha') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      items.sort((a, b) => b.deniedPackets - a.deniedPackets);
    }
    const count = Math.max(1, Math.ceil(items.length * chartSlice));
    return items.slice(0, count);
  }, [hitsByNode, deniedPacketsSort, chartSlice]);

  // Cross-chart click handler — identity is networkName + nodeName (networkName alone isn't unique, e.g. "WAN Network")
  const handleBarClick = (data: { networkName: string; siteName: string; groupIds: string[] }) => {
    if (selectedNetwork?.networkName === data.networkName && selectedNetwork?.nodeName === data.siteName) {
      // Deselect
      setSelectedNetwork(null);
      setExpandedGroups([]);
    } else {
      setSelectedNetwork({
        networkName: data.networkName,
        nodeName: data.siteName,
        groupIds: data.groupIds,
      });
      // Auto-expand matched groups
      setExpandedGroups(data.groupIds);
      // Reset group table state
      setGroupSearch('');
      setGroupPermFilter('');
      setGroupPage(1);
    }
  };

  const clearNetworkFilter = () => {
    setSelectedNetwork(null);
    setExpandedGroups([]);
  };

  const handleExportPdf = async () => {
    if (!summary) return;

    const logoData = await loadLogoForPdf();
    const layout = createBrandedPdf({
      title: 'Security Policies Report',
      subtitle: `Security Score: ${summary.securityScore.toFixed(0)}/100`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Security Score', value: `${summary.securityScore.toFixed(0)}/100` },
        { label: 'Total Rules', value: String(summary.totalFirewallRules) },
        { label: 'Custom Policies', value: String(summary.firewallGroupCount) },
        { label: 'Denied Packets', value: formatBigNum(summary.totalDeniedPackets) },
        { label: 'Custom Rule Coverage', value: `${summary.customRuleCoveragePercent.toFixed(0)}%` },
      ],
      layout.startY,
      layout
    );

    if (groups.length > 0) {
      y = addSectionHeading(layout.doc, 'Firewall Groups', y + 6, layout);
      addBrandedTable(
        layout.doc,
        ['Name', 'Rules', 'Allow', 'Deny', 'Permissive', 'Networks'],
        groups.map((g) => [
          g.name,
          String(g.ruleCount),
          String(g.allowCount),
          String(g.denyCount),
          g.hasOverlyPermissive ? 'Yes' : 'No',
          g.usedBy.length > 0 ? g.usedBy.map((u) => u.networkName).join(', ') : 'Not in use',
        ]),
        y,
        layout
      );
    }

    downloadPdf(layout.doc, exportFilename('security-policies', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!groups.length) return;

    downloadCsv(
      groups.map((g) => ({
        Name: g.name,
        Rules: g.ruleCount,
        Allow: g.allowCount,
        Deny: g.denyCount,
        'Overly Permissive': g.hasOverlyPermissive ? 'Yes' : 'No',
        Networks: g.usedBy.length > 0 ? g.usedBy.map((u) => u.networkName).join(', ') : 'Not in use',
        Nodes: g.usedBy.length > 0 ? g.usedBy.map((u) => u.nodeName).join(', ') : '',
      })),
      exportFilename('security-policies', 'csv'),
      { report: 'Security Policies' }
    );
  };

  if (summaryLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><Shield className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Security Policies</h1>
          <p className="dls-text-muted-foreground">
            Firewall intelligence, security scoring, 2FA adoption, and compliance
          </p>
        </div>
        <div className="dls-flex dls-items-center dls-gap-3">
          <Tabs value={deniedPacketsPeriod} onValueChange={setDeniedPacketsPeriod}>
            <TabsList className="dls-h-7">
              <TabsTrigger value="24h" className="dls-text-xs dls-px-2 dls-py-0.5">24h</TabsTrigger>
              <TabsTrigger value="7d" className="dls-text-xs dls-px-2 dls-py-0.5">7d</TabsTrigger>
              <TabsTrigger value="30d" className="dls-text-xs dls-px-2 dls-py-0.5">30d</TabsTrigger>
              <TabsTrigger value="90d" className="dls-text-xs dls-px-2 dls-py-0.5">90d</TabsTrigger>
            </TabsList>
          </Tabs>
          <ExportMenu
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            label="Export Security"
          />
        </div>
      </div>

      {/* KPI Strip */}
      {summary && (
        <KpiStrip
          items={[
            {
              label: 'Security Score',
              value: summary.securityScore.toFixed(0),
              suffix: '/100',
              valueClassName: getScoreColor(summary.securityScore),
              tooltip: `20% 2FA (${summary.twoFactorAdoptionPercent.toFixed(0)}%) + 15% custom rule coverage (${summary.customRuleCoveragePercent.toFixed(0)}%) + 15% FW posture (${summary.firewallPostureScore}) + 20% password (${(summary.passwordCompliantCount / Math.max(summary.totalUsers, 1) * 100).toFixed(0)}%) + 10% version (${summary.versionCompliancePercent.toFixed(0)}%) + 10% email (${summary.emailVerifiedPercent.toFixed(0)}%) + 10% active accounts`,
            },
            {
              label: 'Count of Rules',
              value: summary.totalFirewallRules.toLocaleString(),
              subtitle: `${summary.rulesPerNode.toFixed(2)} per node – ${summary.rulesPerNetwork.toFixed(2)} per network`,
              tooltip: 'Total active firewall rules across all networks.',
            },
            {
              label: 'Denied Packets',
              value: deniedPacketsPeriodData ? formatBigNum(deniedPacketsPeriodData.deniedPackets) : formatBigNum(summary.totalDeniedPackets),
              subtitle: `${deniedPacketsPeriodData ? formatBytes(deniedPacketsPeriodData.deniedBytes) : formatBytes(summary.totalDeniedBytes)} denied (${deniedPacketsPeriod})`,
              tooltip: 'Denied packets across all firewall-enabled networks during the selected time period.',
            },
            {
              label: 'Custom Policies',
              value: summary.firewallGroupCount,
              subtitle: summary.overlyPermissiveGroupCount > 0
                ? `${summary.overlyPermissiveGroupCount} overly permissive · ${summary.customRuleCoveragePercent.toFixed(0)}% coverage`
                : `${summary.customRuleCoveragePercent.toFixed(0)}% custom rule coverage`,
              tooltip: 'Custom firewall group policies defined by your organization.',
            },
            {
              label: 'Default Policies',
              value: summary.systemGroupCount,
              subtitle: 'system-managed policies',
              tooltip: 'System-managed firewall policies (e.g. cluster-default, ingress-only, egress-only) automatically applied by the platform.',
            },
          ]}
        />
      )}

      {/* 2FA Contextual Banner — 3-tier: green (compliant), amber (partial), red (none) */}
      {summary && (() => {
        const forced = summary.orgPolicy?.twoFactorForced === true;
        const adoption = summary.twoFactorAdoptionPercent;
        const enabled = summary.twoFactorEnabled;
        const total = summary.totalUsers;

        if (forced && adoption >= 100) {
          return (
            <div className="dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-border dls-border-status-online-tertiary dls-bg-status-online-quarternary dls-px-4 dls-py-3">
              <ShieldCheck className="dls-h-5 dls-w-5 dls-text-status-online dls-flex-shrink-0" />
              <p className="dls-text-sm dls-text-status-online">
                2FA enforced by organization policy – all {total} users compliant
              </p>
            </div>
          );
        }

        if (enabled > 0) {
          const msg = forced
            ? `2FA enforced by organization policy – ${enabled} of ${total} users compliant (${adoption.toFixed(0)}%)`
            : `${enabled} of ${total} users have 2FA enabled (${adoption.toFixed(0)}% adoption) – not enforced by policy`;
          return (
            <div className="dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-border dls-border-status-warning-tertiary dls-bg-status-warning-quarternary dls-px-4 dls-py-3">
              <ShieldAlert className="dls-h-5 dls-w-5 dls-text-status-warning dls-flex-shrink-0" />
              <p className="dls-text-sm dls-text-status-warning">{msg}</p>
            </div>
          );
        }

        return (
          <div className="dls-flex dls-items-center dls-gap-3 dls-rounded-lg dls-border dls-border-status-offline-tertiary dls-bg-status-offline-quarternary dls-px-4 dls-py-3">
            <ShieldX className="dls-h-5 dls-w-5 dls-text-status-offline dls-flex-shrink-0" />
            <p className="dls-text-sm dls-text-status-offline">
              No users have 2FA enabled – multi-factor authentication is not active
            </p>
          </div>
        );
      })()}

      {/* Rule type + profile toggles for rules charts */}
      <div className="dls-flex dls-items-center dls-gap-4 dls-flex-wrap">
        <div className="dls-flex dls-items-center dls-gap-2">
          <span className="dls-text-sm dls-text-muted-foreground">Show:</span>
          <Tabs value={String(chartSlice)} onValueChange={(v) => setChartSlice(Number(v))}>
            <TabsList className="dls-h-8">
              <TabsTrigger value="0.1" className="dls-text-xs dls-px-3 dls-py-1">10%</TabsTrigger>
              <TabsTrigger value="0.25" className="dls-text-xs dls-px-3 dls-py-1">25%</TabsTrigger>
              <TabsTrigger value="0.5" className="dls-text-xs dls-px-3 dls-py-1">50%</TabsTrigger>
              <TabsTrigger value="0.75" className="dls-text-xs dls-px-3 dls-py-1">75%</TabsTrigger>
              <TabsTrigger value="1" className="dls-text-xs dls-px-3 dls-py-1">100%</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <span className="dls-text-sm dls-text-muted-foreground">Node Type:</span>
          <Tabs value={rulesProfileFilter} onValueChange={(v) => setRulesProfileFilter(v as 'all' | 'edge' | 'cc')}>
            <TabsList className="dls-h-8">
              <TabsTrigger value="all" className="dls-text-xs dls-px-3 dls-py-1">All</TabsTrigger>
              <TabsTrigger value="edge" className="dls-text-xs dls-px-3 dls-py-1">Edge</TabsTrigger>
              <TabsTrigger value="cc" className="dls-text-xs dls-px-3 dls-py-1">Cloud Connector</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2">
          <span className="dls-text-sm dls-text-muted-foreground">Rule Type:</span>
          <Tabs value={rulesTypeFilter} onValueChange={(v) => { setRulesTypeFilter(v as 'all' | 'system' | 'custom'); if (v !== 'custom') setCustomUsageFilter('all'); }}>
            <TabsList className="dls-h-8">
              <TabsTrigger value="all" className="dls-text-xs dls-px-3 dls-py-1">All</TabsTrigger>
              <TabsTrigger value="system" className="dls-text-xs dls-px-3 dls-py-1">Default</TabsTrigger>
              <TabsTrigger value="custom" className="dls-text-xs dls-px-3 dls-py-1">Custom</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {rulesTypeFilter === 'custom' && (
          <div className="dls-flex dls-items-center dls-gap-2">
            <span className="dls-text-sm dls-text-muted-foreground">Usage:</span>
            <Tabs value={customUsageFilter} onValueChange={(v) => setCustomUsageFilter(v as 'all' | 'in-use' | 'not-in-use')}>
              <TabsList className="dls-h-8">
                <TabsTrigger value="all" className="dls-text-xs dls-px-3 dls-py-1">All</TabsTrigger>
                <TabsTrigger value="in-use" className="dls-text-xs dls-px-3 dls-py-1">In Use</TabsTrigger>
                <TabsTrigger value="not-in-use" className="dls-text-xs dls-px-3 dls-py-1">Not In Use</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}
      </div>

      {/* Network filter chip */}
      {selectedNetwork && (
        <div className="dls-flex dls-items-center dls-gap-2">
          <FilterChip
            label={`Filtering: ${selectedNetwork.networkName}${selectedNetwork.nodeName ? ` (${selectedNetwork.nodeName})` : ''}`}
            onRemove={clearNetworkFilter}
            icon={<Filter className="dls-h-3.5 dls-w-3.5" />}
          />
        </div>
      )}

      {/* Charts: full-width stacked */}
      <div className="dls-grid dls-gap-4">
        {/* Rules Per WAN (stacked bar) */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-items-center dls-justify-between">
              <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-1.5">Rules Per WAN <InfoTooltip text="Allow vs deny rule counts per WAN network interface, sorted by total rules. Click a bar to cross-filter other charts and the firewall groups table." /></CardTitle>
              <Tabs value={wanRulesSort} onValueChange={(v) => setWanRulesSort(v as 'count' | 'alpha')}>
                <TabsList className="dls-h-7">
                  <TabsTrigger value="count" className="dls-text-xs dls-px-2 dls-py-0.5">By Count</TabsTrigger>
                  <TabsTrigger value="alpha" className="dls-text-xs dls-px-2 dls-py-0.5">A–Z</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {wanRulesChartData.length > 0 ? (
              <div className="dls-h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={wanRulesChartData} margin={{ bottom: 5 }} onClick={(state) => {
                    if (state?.activePayload?.[0]?.payload) handleBarClick(state.activePayload[0].payload);
                  }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip
                      content={({ payload, active }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="dls-rounded-md dls-border dls-bg-popover dls-px-3 dls-py-2 dls-text-sm dls-shadow-20">
                            <p className="dls-font-medium">{d.siteName}</p>
                            <p className="dls-text-xs dls-text-muted-foreground">{d.networkName}</p>
                            <p className="dls-mt-1" style={{ color: CORE.darkGreen }}>Allow: {d.Allow}</p>
                            <p style={{ color: NEEVE.orange }}>Deny: {d.Deny}</p>
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Allow" name="Allow" stackId="a" fill={CORE.darkGreen} cursor="pointer">
                      {wanRulesChartData.map((d, i) => (
                        <Cell key={i} fillOpacity={selectedNetwork && !(selectedNetwork.networkName === d.networkName && selectedNetwork.nodeName === d.siteName) ? 0.3 : 1} />
                      ))}
                    </Bar>
                    <Bar dataKey="Deny" name="Deny" stackId="a" fill={NEEVE.orange} cursor="pointer">
                      {wanRulesChartData.map((d, i) => (
                        <Cell key={i} fillOpacity={selectedNetwork && !(selectedNetwork.networkName === d.networkName && selectedNetwork.nodeName === d.siteName) ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dls-h-[320px] dls-flex dls-items-center dls-justify-center dls-text-muted-foreground dls-text-sm">
                No WAN rules found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rules Per LAN (stacked bar) */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-items-center dls-justify-between">
              <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-1.5">Rules Per LAN <InfoTooltip text="Allow vs deny rule counts per LAN/segment network interface, sorted by total rules. Click a bar to cross-filter other charts and the firewall groups table." /></CardTitle>
              <Tabs value={lanRulesSort} onValueChange={(v) => setLanRulesSort(v as 'count' | 'alpha')}>
                <TabsList className="dls-h-7">
                  <TabsTrigger value="count" className="dls-text-xs dls-px-2 dls-py-0.5">By Count</TabsTrigger>
                  <TabsTrigger value="alpha" className="dls-text-xs dls-px-2 dls-py-0.5">A–Z</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {lanRulesChartData.length > 0 ? (
              <div className="dls-h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={lanRulesChartData} margin={{ bottom: 5 }} onClick={(state) => {
                    if (state?.activePayload?.[0]?.payload) handleBarClick(state.activePayload[0].payload);
                  }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip
                      content={({ payload, active }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="dls-rounded-md dls-border dls-bg-popover dls-px-3 dls-py-2 dls-text-sm dls-shadow-20">
                            <p className="dls-font-medium">{d.siteName}</p>
                            <p className="dls-text-xs dls-text-muted-foreground">{d.networkName}</p>
                            <p className="dls-mt-1" style={{ color: CORE.darkGreen }}>Allow: {d.Allow}</p>
                            <p style={{ color: NEEVE.orange }}>Deny: {d.Deny}</p>
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Allow" name="Allow" stackId="a" fill={CORE.darkGreen} cursor="pointer">
                      {lanRulesChartData.map((d, i) => (
                        <Cell key={i} fillOpacity={selectedNetwork && !(selectedNetwork.networkName === d.networkName && selectedNetwork.nodeName === d.siteName) ? 0.3 : 1} />
                      ))}
                    </Bar>
                    <Bar dataKey="Deny" name="Deny" stackId="a" fill={NEEVE.orange} cursor="pointer">
                      {lanRulesChartData.map((d, i) => (
                        <Cell key={i} fillOpacity={selectedNetwork && !(selectedNetwork.networkName === d.networkName && selectedNetwork.nodeName === d.siteName) ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dls-h-[320px] dls-flex dls-items-center dls-justify-center dls-text-muted-foreground dls-text-sm">
                No LAN rules found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Denied Packets by Node (vertical bar) */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-items-center dls-justify-between">
              <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-1.5">Denied Packets by Node <span className="dls-text-xs dls-font-normal dls-text-muted-foreground">(All-time)</span> <InfoTooltip text="Cumulative all-time denied packet counts per network from firewall rules, since rule creation or last node reboot. Click a bar to cross-filter other charts and the firewall groups table." /></CardTitle>
              <Tabs value={deniedPacketsSort} onValueChange={(v) => setDeniedPacketsSort(v as 'count' | 'alpha')}>
                <TabsList className="dls-h-7">
                  <TabsTrigger value="count" className="dls-text-xs dls-px-2 dls-py-0.5">By Count</TabsTrigger>
                  <TabsTrigger value="alpha" className="dls-text-xs dls-px-2 dls-py-0.5">A–Z</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {hitsByNodeData.length > 0 ? (
              <div className="dls-h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hitsByNodeData} margin={{ bottom: 5 }} onClick={(state) => {
                    if (state?.activePayload?.[0]?.payload) handleBarClick(state.activePayload[0].payload);
                  }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-45} textAnchor="end" height={100} />
                    <YAxis tickFormatter={(v) => formatBigNum(v)} />
                    <Tooltip
                      content={({ payload, active }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div className="dls-rounded-md dls-border dls-bg-popover dls-px-3 dls-py-2 dls-text-sm dls-shadow-20">
                            <p className="dls-font-medium">{d.siteName}</p>
                            <p className="dls-text-xs dls-text-muted-foreground">{d.networkName}</p>
                            <p className="dls-mt-1" style={{ color: NEEVE.orange }}>Denied: {formatBigNum(d.deniedPackets)} packets</p>
                            <p className="dls-text-muted-foreground">{formatBytes(d.deniedBytes)}</p>
                            <p className="dls-text-muted-foreground">{d.ruleCount} rules</p>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="deniedPackets" name="Denied Packets" fill={NEEVE.orange} cursor="pointer">
                      {hitsByNodeData.map((d, i) => (
                        <Cell key={i} fillOpacity={selectedNetwork && !(selectedNetwork.networkName === d.networkName && selectedNetwork.nodeName === d.siteName) ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dls-h-[320px] dls-flex dls-items-center dls-justify-center dls-text-muted-foreground dls-text-sm">
                No denied traffic data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Denied Traffic Trend (with period picker) */}
        <Card>
          <CardHeader>
            <div className="dls-flex dls-items-center dls-justify-between">
              <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-1.5">
                Denied Traffic Trend{selectedNetwork ? ` – ${selectedNetwork.networkName}${selectedNetwork.nodeName ? ` (${selectedNetwork.nodeName})` : ''}` : ''}
                <InfoTooltip text="Time-series view of denied packets and bytes across all firewall-enabled networks. When a network is selected via chart click, shows only that network's denied traffic." />
              </CardTitle>
              <Tabs value={deniedTrendPeriod} onValueChange={setDeniedTrendPeriod}>
                <TabsList className="dls-h-7">
                  <TabsTrigger value="24h" className="dls-text-xs dls-px-2 dls-py-0.5">24h</TabsTrigger>
                  <TabsTrigger value="7d" className="dls-text-xs dls-px-2 dls-py-0.5">7d</TabsTrigger>
                  <TabsTrigger value="30d" className="dls-text-xs dls-px-2 dls-py-0.5">30d</TabsTrigger>
                  <TabsTrigger value="90d" className="dls-text-xs dls-px-2 dls-py-0.5">90d</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="dls-px-3">
            {deniedTrendHasData ? (
              <div className="dls-h-[280px]">
                <div className="dls-flex dls-justify-between dls-px-1 dls-mb-0.5">
                  <span className="dls-text-xs dls-text-muted-foreground">Bytes</span>
                  <span className="dls-text-xs dls-text-muted-foreground">Packets</span>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <ComposedChart data={deniedTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tickFormatter={(v) => formatBytes(v)} />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => formatBigNum(v)} />
                    <Tooltip
                      formatter={(value: number | string, name: string) => {
                        if (typeof value !== 'number') return ['No data', name];
                        if (name === 'Bytes') return [formatBytes(value), name];
                        return [formatBigNum(value), name];
                      }}
                    />
                    <Legend />
                    {deniedPartialRegion && (
                      <ReferenceArea
                        yAxisId="left"
                        x1={deniedPartialRegion.x1}
                        x2={deniedPartialRegion.x2}
                        fill={NEUTRAL.quarternary}
                        fillOpacity={0.35}
                        strokeOpacity={0}
                        label={{
                          value: 'Partial data',
                          position: 'insideTopLeft',
                          fontSize: 11,
                          fill: NEUTRAL.secondary,
                        }}
                      />
                    )}
                    <Bar yAxisId="left" dataKey="Bytes" name="Bytes" fill={CORE.darkGreen} />
                    <Line yAxisId="right" dataKey="Packets" name="Packets" stroke={NEEVE.orange} strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="dls-h-[280px] dls-flex dls-items-center dls-justify-center dls-text-muted-foreground dls-text-sm">
                Data accumulates after multiple poll cycles
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Firewall Groups Table */}
      <Card>
        <CardHeader>
          <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
            <CardTitle className="dls-text-base dls-flex dls-items-center dls-gap-1.5">
              Firewall Groups
              <InfoTooltip text="Reusable firewall rule templates applied to network interfaces. Expand a group to see individual rules with priority, action, and traffic hit counters. Click a chart bar above to filter to that network's groups." />
              {selectedNetwork && (
                <span className="dls-text-sm dls-font-normal dls-text-muted-foreground dls-ml-2">
                  – filtered to {selectedNetwork.networkName}{selectedNetwork.nodeName ? ` (${selectedNetwork.nodeName})` : ''}
                </span>
              )}
            </CardTitle>
            {!selectedNetwork && (
              <div className="dls-flex dls-items-center dls-gap-2">
                <div className="dls-relative dls-w-48">
                  <Search className="dls-absolute dls-left-2.5 dls-top-1/2 -dls-translate-y-1/2 dls-h-3.5 dls-w-3.5 dls-text-muted-foreground" />
                  <Input
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => { setGroupSearch(e.target.value); setGroupPage(1); }}
                    className="dls-pl-8 dls-h-8 dls-text-sm dls-rounded-xl"
                  />
                </div>
                <Select value={groupPermFilter} onValueChange={(v) => { setGroupPermFilter(v === 'all' ? '' : v); setGroupPage(1); }}>
                  <SelectTrigger className="dls-h-8 dls-w-[160px] dls-text-sm">
                    <span>{groupPermFilter === 'true' ? 'Overly Permissive' : 'All Groups'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    <SelectItem value="true">Overly Permissive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={groupUsageFilter || 'all'} onValueChange={(v) => { setGroupUsageFilter(v === 'all' ? '' : v as 'in-use' | 'unused'); setGroupPage(1); }}>
                  <SelectTrigger className="dls-h-8 dls-w-[120px] dls-text-sm">
                    <span>{groupUsageFilter === 'in-use' ? 'In Use' : groupUsageFilter === 'unused' ? 'Unused' : 'All Usage'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Usage</SelectItem>
                    <SelectItem value="in-use">In Use</SelectItem>
                    <SelectItem value="unused">Unused</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="dls-p-0 dls-overflow-x-auto">
          <Table className="dls-min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead className="dls-w-8"></TableHead>
                <TableHead>Policy</TableHead>
                <TableHead>Network</TableHead>
                <TableHead>Node</TableHead>
                <TableHead>Rules</TableHead>
                <TableHead>Allow</TableHead>
                <TableHead>Deny</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groupsLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="dls-text-center dls-py-12 dls-text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : selectedNetwork && selectedNetwork.groupIds.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="dls-text-center dls-py-12 dls-text-muted-foreground">
                    No groups assigned to this network
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="dls-text-center dls-py-12 dls-text-muted-foreground">
                    No firewall groups found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((g) => {
                  const isExpanded = expandedGroups.includes(g.id);
                  return (
                    <>
                      <TableRow
                        key={g.id}
                        className="dls-cursor-pointer hover:dls-bg-muted/50"
                        onClick={() => setExpandedGroups((prev) =>
                          prev.includes(g.id) ? prev.filter((id) => id !== g.id) : [...prev, g.id]
                        )}
                      >
                        <TableCell>
                          <ChevronDownIcon
                            className={cn(
                              'dls-h-4 dls-w-4 dls-text-muted-foreground dls-transition-transform',
                              isExpanded && 'dls-rotate-180'
                            )}
                          />
                        </TableCell>
                        <TableCell className="dls-font-medium dls-max-w-[200px] dls-truncate" title={g.name}>
                          {g.name}
                        </TableCell>
                        <TableCell className="dls-max-w-[180px] dls-truncate dls-text-xs" title={[...new Set(g.usedBy.map((u) => u.networkName))].join(', ')}>
                          {(() => {
                            const names = [...new Set(g.usedBy.map((u) => u.networkName))];
                            if (names.length === 0) return <span className="dls-text-muted-foreground/50 dls-italic">Not in use</span>;
                            return <span className="dls-text-muted-foreground">{names.length <= 2 ? names.join(', ') : `${names[0]}, ${names[1]} +${names.length - 2}`}</span>;
                          })()}
                        </TableCell>
                        <TableCell className="dls-max-w-[180px] dls-truncate dls-text-xs" title={[...new Set(g.usedBy.map((u) => u.nodeName).filter(Boolean))].join(', ')}>
                          {(() => {
                            if (g.usedBy.length === 0) return <span className="dls-text-muted-foreground/50 dls-italic">–</span>;
                            const names = [...new Set(g.usedBy.map((u) => u.nodeName).filter(Boolean))];
                            if (names.length === 0) return <span className="dls-text-muted-foreground/50">–</span>;
                            return <span className="dls-text-muted-foreground">{names.length <= 2 ? names.join(', ') : `${names[0]}, ${names[1]} +${names.length - 2}`}</span>;
                          })()}
                        </TableCell>
                        <TableCell>
                          {g.ruleCount}
                          <span className="dls-text-xs dls-text-muted-foreground dls-ml-1">
                            ({g.customRuleCount} custom / {g.systemRuleCount} system)
                          </span>
                        </TableCell>
                        <TableCell className="dls-text-status-online">{g.allowCount}</TableCell>
                        <TableCell className="dls-text-status-offline">{g.denyCount}</TableCell>
                        <TableCell>
                          {g.hasOverlyPermissive ? (
                            <span className="dls-flex dls-items-center dls-gap-1">
                              <Badge variant="destructive" className="dls-text-xs">Overly Permissive</Badge>
                              <InfoTooltip text="A rule is flagged as overly permissive when it ALLOWs traffic from any source (0.0.0.0/0) to any destination across all ports (0–65535), providing no filtering." />
                            </span>
                          ) : (
                            <Badge variant="secondary" className="dls-text-xs">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                      {isExpanded && g.rules.length > 0 && (
                        <TableRow key={`${g.id}-rules`}>
                          <TableCell colSpan={8} className="dls-bg-muted/30 dls-p-0">
                            <div className="dls-px-6 dls-py-3 dls-max-h-[300px] dls-overflow-y-auto">
                              <table className="dls-w-full dls-text-xs">
                                <thead>
                                  <tr className="dls-text-muted-foreground">
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Priority</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Type</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Protocol</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Action</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Source IP</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Src Port</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Dest IP</th>
                                    <th className="dls-text-left dls-py-1 dls-pr-3">Dst Port</th>
                                    <th className="dls-text-right dls-py-1 dls-pr-3">Packets</th>
                                    <th className="dls-text-right dls-py-1">Bytes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {g.rules.map((r, i) => {
                                    const isSystem = r.priority < 1000 || r.priority > 10000;
                                    const packets = parseInt(r.packetCount, 10) || 0;
                                    const bytes = parseInt(r.byteCount, 10) || 0;
                                    return (
                                      <tr key={i} className="dls-border-t dls-border-muted">
                                        <td className="dls-py-1 dls-pr-3">{r.priority}</td>
                                        <td className="dls-py-1 dls-pr-3">
                                          <Badge variant={isSystem ? 'secondary' : 'outline'} className="dls-text-[10px] dls-px-1.5 dls-py-0">
                                            {isSystem ? 'System' : 'Custom'}
                                          </Badge>
                                        </td>
                                        <td className="dls-py-1 dls-pr-3">{r.protocol || 'any'}</td>
                                        <td className={cn('dls-py-1 dls-pr-3 dls-font-medium', r.action === 'ALLOW' ? 'dls-text-status-online' : 'dls-text-status-offline')}>
                                          {r.action}
                                        </td>
                                        <td className="dls-py-1 dls-pr-3 dls-font-mono">{r.sourceIp || 'any'}</td>
                                        <td className="dls-py-1 dls-pr-3">
                                          {r.sourcePortStart === 0 && r.sourcePortEnd === 0 ? 'any' : `${r.sourcePortStart}–${r.sourcePortEnd}`}
                                        </td>
                                        <td className="dls-py-1 dls-pr-3 dls-font-mono">
                                          {r.destinationIp || r.destinationNetwork || 'any'}
                                        </td>
                                        <td className="dls-py-1 dls-pr-3">
                                          {r.destinationPortStart === 0 && r.destinationPortEnd === 0 ? 'any' : `${r.destinationPortStart}–${r.destinationPortEnd}`}
                                        </td>
                                        <td className="dls-py-1 dls-pr-3 dls-text-right dls-font-mono">
                                          {packets > 0 ? formatBigNum(packets) : '–'}
                                        </td>
                                        <td className="dls-py-1 dls-text-right dls-font-mono">
                                          {bytes > 0 ? formatBytes(bytes) : '–'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  );
                })
              )}
            </TableBody>
          </Table>

          {!selectedNetwork && groupTotalPages > 1 && (
            <div className="dls-flex dls-items-center dls-justify-between dls-px-4 dls-py-3 dls-border-t">
              <p className="dls-text-sm dls-text-muted-foreground">
                Showing {(groupPage - 1) * groupLimit + 1}–{Math.min(groupPage * groupLimit, groupTotal)} of {groupTotal}
              </p>
              <div className="dls-flex dls-items-center dls-gap-1">
                <Button variant="ghost" size="icon" className="dls-h-8 dls-w-8" onClick={() => setGroupPage(1)} disabled={groupPage === 1}>
                  <ChevronsLeft className="dls-h-4 dls-w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="dls-h-8 dls-w-8" onClick={() => setGroupPage(groupPage - 1)} disabled={groupPage === 1}>
                  <ChevronLeft className="dls-h-4 dls-w-4" />
                </Button>
                <span className="dls-text-sm dls-mx-2">Page {groupPage} of {groupTotalPages}</span>
                <Button variant="ghost" size="icon" className="dls-h-8 dls-w-8" onClick={() => setGroupPage(groupPage + 1)} disabled={groupPage === groupTotalPages}>
                  <ChevronRight className="dls-h-4 dls-w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="dls-h-8 dls-w-8" onClick={() => setGroupPage(groupTotalPages)} disabled={groupPage === groupTotalPages}>
                  <ChevronsRight className="dls-h-4 dls-w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>



    </div>
  );
}
